import { z } from 'zod';

/**
 * Phase 4 chunk 6 — Policy Center DTOs.
 *
 * Mirror of PolicyService input + PolicyProjection / PolicyVersionProjection
 * shapes.  Both sides stay in lockstep — adding a field requires touching
 * this file AND the service AND the DB column (migration 0073).
 *
 * See docs/ai-platform/05-GOVERNANCE.md § 3.
 */

export const PolicyType = z.enum([
  'ai_behavior',
  'approval',
  'notification',
  'routing',
  'workflow',
  'tier_gate',
  'data_classification',
]);
export type PolicyTypeValue = z.infer<typeof PolicyType>;

/**
 * The trigger_condition + action JSONB shapes are intentionally
 * permissive at the DTO layer — the runtime evaluator + the per-type
 * action handler enforce stricter shape per policy type.
 */
const JsonObject = z.record(z.unknown());

export const CreatePolicySchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  type: PolicyType,
  /** Initial body — if omitted, the policy is created without a
   *  version (cannot be enabled until the body lands via editPolicy). */
  triggerCondition: JsonObject.optional(),
  action: JsonObject.optional(),
  notes: z.string().max(2000).optional(),
});
export type CreatePolicyInput = z.infer<typeof CreatePolicySchema>;

export const EditPolicySchema = z.object({
  triggerCondition: JsonObject,
  action: JsonObject,
  notes: z.string().max(2000).optional(),
});
export type EditPolicyInput = z.infer<typeof EditPolicySchema>;

export const SetPolicyEnabledSchema = z.object({
  enabled: z.boolean(),
});
export type SetPolicyEnabledInput = z.infer<typeof SetPolicyEnabledSchema>;

export const PolicySchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  type: PolicyType,
  currentVersionId: z.string().uuid().nullable(),
  currentVersion: z.number().int().positive().nullable(),
  enabled: z.boolean(),
  runtimeSupport: z.enum(['enforced', 'draft_only']),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PolicyProjection = z.infer<typeof PolicySchema>;

export const PolicyVersionSchema = z.object({
  id: z.string().uuid(),
  policyId: z.string().uuid(),
  version: z.number().int().positive(),
  triggerCondition: JsonObject,
  action: JsonObject,
  notes: z.string().nullable(),
  changedBy: z.string().uuid().nullable(),
  changedAt: z.string().datetime(),
  supersededAt: z.string().datetime().nullable(),
});
export type PolicyVersionProjection = z.infer<typeof PolicyVersionSchema>;

export const PolicyListResponseSchema = z.object({
  items: z.array(PolicySchema),
});
export type PolicyListResponse = z.infer<typeof PolicyListResponseSchema>;

export const PolicyRuntimeEffect = z.enum([
  'allow',
  'deny',
  'require_approval',
]);
export type PolicyRuntimeEffectValue = z.infer<typeof PolicyRuntimeEffect>;

export const PolicyRuntimeHttpRouteSchema = z.object({
  method: z.literal('POST'),
  path: z.string().startsWith('/api/'),
  controller: z.string().min(1),
  handler: z.string().min(1),
});

export const PolicyRuntimeEntrypointSchema = z.object({
  transport: z.enum(['mcp', 'internal']),
  name: z.string().min(1),
});

export const PolicyRuntimeActionCapabilitySchema = z.object({
  action: z.string().min(1),
  policyType: PolicyType,
  effects: z.array(PolicyRuntimeEffect).min(1),
  enforcementBoundary: z.string().min(1),
  httpRoutes: z.array(PolicyRuntimeHttpRouteSchema).min(1),
  entrypoints: z.array(PolicyRuntimeEntrypointSchema),
});

export const PolicyCapabilitiesResponseSchema = z.object({
  schemaVersion: z.literal(1),
  policyTypes: z.array(z.object({
    type: PolicyType,
    runtimeSupport: z.enum(['enforced', 'draft_only']),
  })),
  supportedActions: z.array(PolicyRuntimeActionCapabilitySchema),
});
export type PolicyCapabilitiesResponse = z.infer<
  typeof PolicyCapabilitiesResponseSchema
>;

export const PolicyVersionListResponseSchema = z.object({
  items: z.array(PolicyVersionSchema),
});
export type PolicyVersionListResponse = z.infer<
  typeof PolicyVersionListResponseSchema
>;
